/**
 * RecordReceiptDialog — reusable dialog for recording a new inventory receipt
 * (a batch arriving at a warehouse on a specific date).
 *
 * Writes to goods_receipts + goods_receipt_items via POST /api/stock-levels/warehouse/adjust.
 * Does NOT handle adjustments (damage/loss/etc) — those go through RecordAdjustmentDialog.
 *
 * Semantics:
 *   - User enters "Quantity Received" (the number of units arriving).
 *   - Dialog computes new_quantity = currentQuantity + quantityReceived before submitting,
 *     because the backend API expects the total stock after receipt.
 *   - The backend then books the DIFFERENCE (new_quantity - its own current stock) as the
 *     receipt quantity, so what actually lands on the goods receipt is the units received.
 *
 * Props:
 *   - open, onClose: dialog visibility control
 *   - sku, warehouseCode, currentQuantity: context (read-only)
 *   - brandId: REQUIRED — sent as brand_id. The receipt is stamped with it, and the stock
 *     engine filters goods receipts by brand_id, so an unstamped receipt is invisible to
 *     every brand-scoped stock view.
 *   - onSuccess?(result): called after a successful write; parent can refetch
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { adjustWarehouseStock, formatNumber } from '../../services/inventoryService';

function RecordReceiptDialog({
  open,
  onClose,
  sku,
  warehouseCode,
  currentQuantity,
  brandId,
  onSuccess,
}) {
  const todayStr = () => new Date().toISOString().slice(0, 10);

  const [receiptDate, setReceiptDate] = useState(todayStr);
  const [quantityReceived, setQuantityReceived] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form whenever the dialog opens — each open should be a fresh entry
  useEffect(() => {
    if (open) {
      setReceiptDate(todayStr());
      setQuantityReceived('');
      setUnitCost('');
      setBatchNumber('');
      setExpiryDate('');
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    const receivedQtyParsed = parseFloat(quantityReceived);
    if (quantityReceived === '' || isNaN(receivedQtyParsed)) {
      setError('Quantity Received is required');
      return;
    }
    if (receivedQtyParsed <= 0) {
      setError('Quantity Received must be greater than 0');
      return;
    }

    if (!brandId) {
      setError('No brand selected — cannot record a receipt without a brand.');
      return;
    }

    const newTotal = (currentQuantity || 0) + receivedQtyParsed;

    setSubmitting(true);
    setError(null);
    try {
      const result = await adjustWarehouseStock({
        sku,
        warehouse_code: warehouseCode,
        brand_id: brandId,
        new_quantity: newTotal,
        unit_cost: unitCost === '' ? null : parseFloat(unitCost),
        expiry_date: expiryDate || null,
        batch_number: batchNumber.trim() || null,
        reason: reason.trim() || 'Manual batch receipt',
        receipt_date: receiptDate,
      });
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (err) {
      console.error('Failed to record receipt:', err);
      setError(err.response?.data?.error || err.message || 'Failed to record receipt');
    } finally {
      setSubmitting(false);
    }
  };

  const receivedParsed = quantityReceived !== '' && !isNaN(parseFloat(quantityReceived))
    ? parseFloat(quantityReceived)
    : null;
  const projectedTotal = receivedParsed !== null && receivedParsed > 0
    ? (currentQuantity || 0) + receivedParsed
    : null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Record Receipt</Typography>
          <Typography variant="body2" color="text.secondary">
            {sku} @ {warehouseCode}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
            <Typography variant="caption" color="text.secondary">Current system stock</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {formatNumber(currentQuantity || 0)}
            </Typography>
          </Box>

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Receipt Date"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={submitting}
            helperText="Date the inventory was received at the warehouse"
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Quantity Received"
            value={quantityReceived}
            onChange={(e) => setQuantityReceived(e.target.value)}
            placeholder="Units received in this batch"
            disabled={submitting}
            helperText="Number of new units arriving. Will be added to current stock."
            inputProps={{ min: 0, step: 'any' }}
          />

          {projectedTotal !== null && (
            <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="caption" color="text.secondary">After receipt</Typography>
              <Typography variant="body1" fontWeight="medium" color="success.main">
                New stock: {formatNumber(projectedTotal)}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({formatNumber(currentQuantity || 0)} + {formatNumber(receivedParsed)})
                </Typography>
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Unit Cost (optional)"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            placeholder="Cost per unit"
            disabled={submitting}
            helperText="Cost per unit in the brand's currency. Leave blank if not tracked."
            inputProps={{ min: 0, step: 'any' }}
          />

          <TextField
            fullWidth
            size="small"
            label="Batch Number (optional)"
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder="Auto-generated if blank (e.g. SYS-ADJ-20260422-42)"
            disabled={submitting}
            helperText="Identifier for this specific batch. Optional."
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label="Expiry Date (optional)"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={submitting}
            helperText="When this batch expires, if applicable."
          />

          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Notes"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. PO #1234, factory shipment, sample, return-to-stock, etc."
            disabled={submitting}
            helperText="Recommended — captured on the receipt record"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || quantityReceived === ''}
          color="success"
        >
          {submitting && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
          Record Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RecordReceiptDialog;
